import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";

import { Navbar } from "../shared/components/navbar/Navbar.js";
import { LcapiiService } from "../shared/services/LcapiiService.js";
import { RegistroService } from "../shared/services/RegistroService.js";
import { SucursalesService } from "../shared/services/SucursalesService.js";
import { Loading, showMessage, mostrarResultado } from "../shared/ui/ui.js";

// Un único controller para todas las operaciones: siguen el mismo molde
// (entradas → validación → algoritmo → registro → resultado) y lo que cambia
// está declarado en operaciones/index.js. Una operación nueva no necesita
// controller propio.
//
// Dos puntos opcionales del molde: `calcular` puede ser async, y si el cálculo
// exporta `interpretar` se muestra su resultado en #interpretacion mientras se
// tipea (vista previa de lo que se va a habilitar).

const valorDe = (id) => document.getElementById(id)?.value ?? "";

export const OperacionController = {
  async render(operacion) {
    document.getElementById("app").innerHTML = Navbar.render(operacion.clave) + operacion.plantilla;
    Navbar.bind();

    document.getElementById("formCalculo")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      await OperacionController.calcular(operacion);
    });

    OperacionController.prepararFechas(operacion);
    OperacionController.prepararInterpretacion(operacion);
    await OperacionController.cargarSucursales(operacion);
  },

  prepararInterpretacion(operacion) {
    const destino = document.getElementById("interpretacion");
    if (!operacion.calculo.interpretar || !destino) return;

    document.getElementById("formCalculo")?.addEventListener("input", () => {
      destino.textContent = operacion.calculo.interpretar(operacion.leer(valorDe), new Date()) ?? "";
    });
  },

  prepararFechas(operacion) {
    if (!operacion.fechas?.length) return;

    flatpickr.localize(Spanish);
    operacion.fechas.forEach((id) => {
      const input = document.getElementById(id);
      if (!input) return;
      flatpickr(input, {
        maxDate: new Date().fp_incr(2),
        minDate: new Date().fp_incr(-2),
        dateFormat: "d-m-Y",
        defaultDate: new Date(),
        position: "above",
      });
    });
  },

  async cargarSucursales(operacion) {
    if (!operacion.selects?.length) return;

    try {
      Loading();
      const sesion = LcapiiService.sesion();
      const todas = await SucursalesService.listar(sesion.sucursalesAlias);

      operacion.selects.forEach(({ id, origen }) => {
        const cbo = document.getElementById(id);
        if (!cbo) return;

        const permitidas = SucursalesService.filtrarPermitidas(
          todas,
          sesion[origen],
          sesion.sucursalesExcluidas,
        );
        permitidas.forEach((suc) => {
          const opcion = document.createElement("option");
          opcion.value = suc.id;
          opcion.text = suc.etiqueta;
          // El registro congela el nombre de LCApii, no el que se muestra.
          opcion.dataset.nombre = suc.nombre;
          cbo.appendChild(opcion);
        });

        if (permitidas.length === 0) {
          cbo.disabled = true;
          showMessage(
            "No tenés sucursales habilitadas para esta operación. Pedí que te las carguen en LCApii.",
            "error",
            6000,
          );
        }
      });
    } catch (error) {
      showMessage(error, "error");
    } finally {
      Loading(false);
    }
  },

  async calcular(operacion) {
    Loading();
    // Se toma el formulario antes de mostrar el resultado: el diálogo espera al
    // usuario y, si mientras tanto cambió de sección, el reset caería sobre el
    // formulario de otra operación.
    const formulario = document.getElementById("formCalculo");
    try {
      const entradas = operacion.leer(valorDe);

      const errorValidacion = operacion.calculo.validar?.(entradas);
      if (errorValidacion) {
        showMessage(errorValidacion, "error");
        return;
      }

      const { codigo, detalle, truncado } = await operacion.calculo.calcular(entradas, new Date());

      const sucursal = operacion.sucursalPrincipal
        ? document.getElementById(operacion.sucursalPrincipal)
        : null;

      const estado = await RegistroService.registrar({
        accion: operacion.accion,
        codigo: String(codigo),
        sucursalId: sucursal?.value ? Number(sucursal.value) : null,
        sucursalNombre: sucursal?.selectedOptions?.[0]?.dataset.nombre || null,
        detalle,
      });

      const avisos = [];
      // El original mostraba un código incompleto como si fuera válido.
      if (truncado) avisos.push("El código se generó incompleto. Verificá los datos antes de usarlo.");
      if (estado === "encolado") avisos.push("Sin conexión: el registro se enviará cuando vuelva la red.");
      if (estado === "descartado") avisos.push("El registro de auditoría no pudo guardarse.");

      await mostrarResultado({
        titulo: operacion.titulo,
        codigo,
        aviso: avisos.join(" "),
      });

      if (formulario?.isConnected) formulario.reset();
    } catch (error) {
      console.error(error);
      showMessage("No se pudo calcular el código. Revisá los datos ingresados.", "error");
    } finally {
      Loading(false);
    }
  },
};
